import { useMemo, useEffect, useState, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Link } from "react-router";
import {
  History,
  CreditCard,
  Activity,
  ExternalLink,
  ArrowRight,
  ArrowUpRight,
  Clock,
  Wallet,
  Mail,
  TrendingUp,
  TrendingDown,
  X,
  Loader2,
} from "lucide-react";
import { useTheme } from "next-themes";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  useAppUsageStats,
  useServiceUsageStats,
  useUsageStatsLoading,
  useDashboardStore,
  useDashboardOverview,
  useOverviewLoading,
  useRefreshOverview,
} from "../../stores/modules/dashboard";
import {
  useAlertEmail,
  useSettingsLoading,
  useSettingsLoaded,
  useRefreshSettings,
  useSetAlertEmail,
} from "../../stores/modules/settings";
import { useWalletSignature } from "../hooks/useWalletSignature";
import {
  classifyWalletError,
  getWalletErrorMessageKey,
} from "@/utils/walletError";
import { useDepositBlockchainsStore } from "../../stores/modules/depositBlockchains";
import {
  getFundTransactions,
  cancelSubscription,
  createWithdrawal,
  updateNotificationEmail,
  type FundTransaction,
} from "@/api";
import { RechargeModal } from "../components/RechargeModal";
import { NetworkInfoCard, NetworkInfoCardSkeleton } from "../components/NetworkInfoCard";
import { TablePaginationFooter } from "../components/data-table/TablePaginationFooter";
import { DataTableStateRow } from "../components/data-table/DataTableStateRow";
import { DataTableLoadingOverlay } from "../components/data-table/DataTableLoadingOverlay";
import { formatUsdtBalance } from "@/utils";
import { tryM } from "@/utils/money";
import {
  sanitizeWithdrawAmountInput,
  isValidWithdrawAmountForSubmit,
} from "@/utils/withdrawAmountInput";
import { DashboardAnnouncementBar } from "../components/announcements/DashboardAnnouncementBar";
import { useAnnouncements } from "../hooks/useAnnouncements";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** 资金流水表每页条数（服务端分页） */
const TX_PAGE_SIZE = 10;
/** 仪表盘公告栏最多展示条数 */
const DASHBOARD_ANNOUNCEMENT_LIMIT = 3;

/** Unix 秒 → "YYYY-MM-DD HH:mm:ss" */
function formatDateTime(unixSec: number, withSeconds = true): string {
  if (!unixSec || !Number.isFinite(unixSec)) return "—";
  const d = new Date(unixSec * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}${
    withSeconds ? `:${pad(d.getSeconds())}` : ""
  }`;
  return `${date} ${time}`;
}

/** 用量类数字：兼容本地持久化里缺字段或非数字（避免 `undefined.toLocaleString`） */
function safeUsageInt(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Balance formatter for dashboard: truncates to 4 decimals without rounding,
 * delegating to the shared `formatUsdtBalance` util.
 */
function formatUsdt(value: number | undefined): string {
  return formatUsdtBalance(value);
}

/** App Key 用量柱状图：每条类目最小宽度下限（px）；长名称时由 `usageChartSlotWidthPx` 抬高 */
const USAGE_CHART_MIN_SLOT_PX = 56;
/** 底部标签：最多 2 行，每行最大字符数；超出在第二行末加省略号（全文见 Tooltip） */
const USAGE_CHART_WRAP_CHARS = 18;
/** 估算每字符占位（px，10px 刻度字体近似），用于计算横向滚动最小宽度 */
const USAGE_CHART_CHAR_PX = 5.65;

function wrapUsageChartAxisLabel(text: string): string[] {
  const s = String(text ?? "");
  const w = USAGE_CHART_WRAP_CHARS;
  if (!s) return [""];
  if (s.length <= w) return [s];
  if (s.length <= 2 * w) {
    return [s.slice(0, w), s.slice(w)];
  }
  return [s.slice(0, w), `${s.slice(w, w + w - 1)}…`];
}

/** 取当前数据下每条类目所需最小横向宽度，保证换行后的最长行不挤进邻柱 */
function usageChartSlotWidthPx(names: Iterable<string>): number {
  let max = USAGE_CHART_MIN_SLOT_PX;
  for (const name of names) {
    const lines = wrapUsageChartAxisLabel(name);
    const longest = Math.max(1, ...lines.map((l) => l.length));
    const w = longest * USAGE_CHART_CHAR_PX + 24;
    max = Math.max(max, Math.min(560, Math.ceil(w)));
  }
  return max;
}

/** Y 轴刻度：K=10³, M=10⁶, B=10⁹, T=10¹²；小于 1000 用整数 */
function formatAxisCount(n: number): string {
  if (!Number.isFinite(n)) return "";
  if (n === 0) return "0";
  const sign = n < 0 ? "-" : "";
  const v = Math.abs(n);
  if (v < 1000) return sign + String(Math.round(v));

  const tiers = [
    [1e12, "T"],
    [1e9, "B"],
    [1e6, "M"],
    [1e3, "K"],
  ] as const;
  for (const [base, suf] of tiers) {
    if (v >= base) {
      const x = v / base;
      const decimals = x >= 100 ? 0 : x >= 10 ? 1 : 2;
      let s = x.toFixed(decimals);
      s = s.replace(/\.0+$/, "").replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
      return `${sign}${s}${suf}`;
    }
  }
  return sign + String(Math.round(v));
}

/** 线性 Y 轴：均匀刻度约 5–6 段，上限取整到「好看」步长 */
function buildUsageChartYScale(values: number[]): { niceMax: number; ticks: number[] } {
  const maxUsage = Math.max(0, ...values);
  if (maxUsage === 0) {
    return { niceMax: 4, ticks: [0, 1, 2, 3, 4] };
  }
  const targetSegments = 5;
  const roughStep = maxUsage / targetSegments;
  const exp = Math.floor(Math.log10(roughStep));
  const magnitude = 10 ** exp;
  const normalized = roughStep / magnitude;
  let niceUnit: number;
  if (normalized <= 1) niceUnit = 1;
  else if (normalized <= 2) niceUnit = 2;
  else if (normalized <= 5) niceUnit = 5;
  else niceUnit = 10;
  const step = niceUnit * magnitude;
  const niceMax = Math.max(step, Math.ceil(maxUsage / step) * step);
  const ticks: number[] = [];
  for (let x = 0; x <= niceMax + step * 1e-9; x += step) {
    const v = step >= 1 ? Math.round(x) : Number(x.toPrecision(10));
    if (ticks.length && ticks[ticks.length - 1] === v) continue;
    ticks.push(v);
    if (ticks.length > 16) break;
  }
  return { niceMax, ticks };
}

/** 与柱图、固定 Y 轴列对齐的 margin（顶/底一致以保证 y=0 对齐） */
const USAGE_CHART_MARGIN_TOP = 10;
/**
 * 底部：X 轴 `height` 略大于两行 10px 刻度 + 首行 dy，避免 label 下多余留白。
 */
const USAGE_CHART_X_AXIS_HEIGHT = 34;
/** 底部类目首行与柱底之间的额外间距（通过首行 tspan 的 dy 实现） */
const USAGE_CHART_X_TICK_GAP_AFTER_BARS = 10;
/** 刻度区与容器底之间极小间隙即可 */
const USAGE_CHART_MARGIN_BOTTOM = 2;

/** Y 轴刻度 11px 下估算每字符宽度，用于左列自适应 */
const USAGE_Y_TICK_CHAR_WIDTH_PX = 6.05;

/** 左列总宽与内部 YAxis.width：随当前刻度文案（K/M/B/T）长度变化 */
function estimateUsageYAxisColumnLayout(ticks: number[]): { colPx: number; yAxisWidth: number } {
  let maxLen = 1;
  for (const t of ticks) {
    maxLen = Math.max(maxLen, formatAxisCount(t).length);
  }
  const inner = Math.ceil(maxLen * USAGE_Y_TICK_CHAR_WIDTH_PX + 8);
  const yAxisWidth = Math.min(96, Math.max(34, inner));
  const colPx = yAxisWidth + 6;
  return { colPx, yAxisWidth };
}

/**
 * 根据 `amountUsdt` 字符串自身的符号判定正负并格式化显示。
 *
 * 服务端对支出类交易（withdraw / subscription_charge）返回的 `amountUsdt`
 * 已经带 "-" 前缀，这里直接沿用；正值则额外补一个 "+" 视觉提示。
 */
function txAmountDisplay(tx: FundTransaction): { text: string; positive: boolean } {
  const raw = String(tx.amountUsdt ?? "0").trim();
  const isNegative = raw.startsWith("-");
  const positive = !isNegative;
  const text = positive ? `+${raw} USDT` : `${raw} USDT`;
  return { text, positive };
}

export function Dashboard() {
  const { t } = useTranslation();
  const { announcements } = useAnnouncements(1, DASHBOARD_ANNOUNCEMENT_LIMIT);
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const appUsageStats = useAppUsageStats();
  const serviceUsageStats = useServiceUsageStats();
  const isLoadingUsageStats = useUsageStatsLoading();
  const { refreshAll } = useDashboardStore();
  const { getWalletSignature } = useWalletSignature();
  const depositBlockchains = useDepositBlockchainsStore((s) => s.blockchains);
  const depositBlockchainsStatus = useDepositBlockchainsStore((s) => s.status);

  // Dashboard 主数据：统一从 store 读取（与顶栏 useUsdtBalance 同源），
  // 避免组件本地重复维护导致 stale
  const overview = useDashboardOverview();
  const overviewLoading = useOverviewLoading();
  const refreshOverview = useRefreshOverview();

  // 资金流水（服务端分页）
  const [transactions, setTransactions] = useState<FundTransaction[]>([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txPage, setTxPage] = useState(1);
  const [txTotal, setTxTotal] = useState(0);

  // 告警邮箱由 settings store 提供：登录态进入 Layout 时已由 useAuthedBootstrap 预热；
  // 这里只读 + 在保存后主动 refresh / setAlertEmail，避免每次进入 Dashboard 都重发 /settings
  const currentAlertEmail = useAlertEmail();
  const settingsLoading = useSettingsLoading();
  const settingsLoaded = useSettingsLoaded();
  const refreshSettings = useRefreshSettings();
  const setAlertEmail = useSetAlertEmail();
  // 仅在「从未拉过 /settings」时显示 skeleton，避免后续刷新整块回到 skeleton 状态闪烁
  const isLoadingSettings = settingsLoading && !settingsLoaded;

  // Modal & 表单状态
  const [isRechargeModalOpen, setIsRechargeModalOpen] = useState(false);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [isEmailAlertModalOpen, setIsEmailAlertModalOpen] = useState(false);
  const [emailAlert, setEmailAlert] = useState("");
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  const [isStoppingSubscription, setIsStoppingSubscription] = useState(false);

  const refreshTransactions = useCallback(async (page: number = 1) => {
    setTxLoading(true);
    try {
      const res = await getFundTransactions({ page, limit: TX_PAGE_SIZE });
      if (res.ok) {
        setTransactions(res.data.transactions ?? []);
        setTxTotal(res.data.total ?? 0);
        setTxPage(res.data.page ?? page);
      } else {
        console.error("[Dashboard] getFundTransactions failed:", res.error);
      }
    } finally {
      setTxLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshAll();
    void refreshOverview();
    void refreshTransactions(1);
  }, [refreshAll, refreshOverview, refreshTransactions]);

  // ── 字段派生 ──
  // 注：全链 optional chain 仅用于 overview 尚未加载时避免 runtime TypeError。
  // balance 现以 `availableUsdt / lockedUsdt` 字符串形式返回，这里 parse 为 number 供 UI 使用。
  const subscription = overview?.subscription;
  const balanceAvailable = useMemo(() => {
    // Decimal-safe parse from API string. Result is converted to number for the
    // existing `sanitizeWithdrawAmountInput` API surface; precision-critical
    // checks inside that helper now run on Decimal again, so any number-side
    // drift here only affects display, never gating logic.
    const d = tryM(overview?.balance?.availableUsdt);
    return d === null ? 0 : d.toNumber();
  }, [overview]);
  const currentPlan = subscription?.planName ?? "—";
  const remainingCredits = useMemo(() => {
    const v = overview?.credits?.remaining;
    if (v === undefined) return "—";
    return v.toLocaleString();
  }, [overview]);
  const planExpiry = useMemo(() => {
    if (!subscription?.contractEndAt) return "—";
    return formatDateTime(subscription.contractEndAt, false);
  }, [subscription]);
  const nextCreditResetDate = useMemo(() => {
    if (!subscription?.nextCreditResetAt) return "—";
    return formatDateTime(subscription.nextCreditResetAt, false);
  }, [subscription]);

  // yaml 已移除 `cancelAtPeriodEnd` 字段，改由 subscriptionState 状态机派生：
  // - paid_pending_change + pendingPlan.isFree=true   → 周期末取消订阅（降级到 Free）
  // - paid_pending_change + pendingPlan.isFree=false  → 周期末降级到付费低档
  // - free_active / free_past_due                      → 当前免费计划（无需取消）
  const subscriptionState = subscription?.subscriptionState;
  const pendingPlanIsFree = subscription?.pendingPlan?.isFree === true;
  const isPendingChange = subscriptionState === "paid_pending_change";
  const willCancelAtPeriodEnd = isPendingChange && pendingPlanIsFree;
  const isFreePlan =
    subscriptionState === "free_active" ||
    subscriptionState === "free_past_due";
  const isSubscriptionActive =
    subscription?.status === "active" && !willCancelAtPeriodEnd;
  const pendingPlan = subscription?.pendingPlan
    ? {
        name: subscription.pendingPlan.planName,
        isFree: subscription.pendingPlan.isFree,
        effectiveDate: subscription.pendingPlan.effectiveAt
          ? formatDateTime(subscription.pendingPlan.effectiveAt, false)
          : planExpiry,
      }
    : null;

  /** 提现弹窗：网络信息严格来自 GET /deposit/blockchains 的第 0 项 */
  const withdrawChain = depositBlockchains[0] ?? null;
  const isWithdrawNetworkLoading =
    depositBlockchainsStatus === "idle" ||
    depositBlockchainsStatus === "loading" ||
    !withdrawChain;

  const isWithdrawAmountSubmittable = useMemo(
    () => isValidWithdrawAmountForSubmit(withdrawAmount, balanceAvailable),
    [withdrawAmount, balanceAvailable]
  );

  // 资金流水：服务端已从 FundTransactionType 中移除 usage_charge，
  // 这里只保留 recharge / withdraw / subscription_charge 的展示无需再过滤。
  const txTotalPages = Math.max(1, Math.ceil(txTotal / TX_PAGE_SIZE));

  // 当条数因撤销订阅 / 退款等变化使 txTotalPages 缩小到小于 txPage 时，
  // 自动回退到最后一页（Ant Design `Pagination` 同样会 clamp，避免停留在空白页）。
  useEffect(() => {
    if (txPage > txTotalPages) {
      void refreshTransactions(txTotalPages);
    }
  }, [txTotalPages, txPage, refreshTransactions]);

  // 图表数据：空数组时由调用侧渲染空态，避免用占位数据误导用户
  const usageData = useMemo(
    () =>
      appUsageStats.map((stat) => ({
        name: stat.appName || stat.appId || "—",
        usage: safeUsageInt(stat.totalCredits),
      })),
    [appUsageStats]
  );
  const hasUsageData = usageData.length > 0;
  // 仅在「首次」拉取用量排行时展示 loading：后续刷新保留已有柱图，避免闪烁
  const isUsageChartFirstLoading = isLoadingUsageStats && !hasUsageData;

  const usageChartMinSlotComputed = useMemo(
    () => usageChartSlotWidthPx(usageData.map((d) => d.name)),
    [usageData]
  );

  const usageChartY = useMemo(
    () => buildUsageChartYScale(usageData.map((d) => d.usage)),
    [usageData]
  );

  const usageChartYAxisLayout = useMemo(
    () => estimateUsageYAxisColumnLayout(usageChartY.ticks),
    [usageChartY]
  );

  const renderUsageChartXTick = useCallback(
    (props: { x: number; y: number; payload: { value?: string } }) => {
      const { x, y, payload } = props;
      const lines = wrapUsageChartAxisLabel(String(payload?.value ?? ""));
      const fill = isDark ? "#606566" : "#64748b";
      return (
        <g transform={`translate(${x},${y})`}>
          <text textAnchor="middle" fill={fill} fontSize={10}>
            {lines.map((line, i) => (
              <tspan key={i} x={0} dy={i === 0 ? USAGE_CHART_X_TICK_GAP_AFTER_BARS : 10}>
                {line}
              </tspan>
            ))}
          </text>
        </g>
      );
    },
    [isDark]
  );

  const serviceUsage = useMemo(() => {
    if (serviceUsageStats.length === 0) return [];
    return serviceUsageStats.map((stat, index) => ({
      id: index + 1,
      name: stat.serviceName ?? "—",
      requests: safeUsageInt(stat.totalCalls).toLocaleString(),
      trend: typeof stat.trend === "string" ? stat.trend : "—",
    }));
  }, [serviceUsageStats]);

  // ── 操作回调 ──
  const handleStopSubscription = async () => {
    if (isStoppingSubscription) return;
    if (isFreePlan) return;
    // 已有待生效降级计划时禁止再次「停止订阅」（避免覆盖后端 pendingPlan 状态）
    if (isPendingChange) {
      toast.warning(t("dashboard.tooltipPendingChangeLocked"));
      return;
    }
    setIsStoppingSubscription(true);
    const toastId = "stop-subscription";
    toast.loading(t("dashboard.stopSubscription"), { id: toastId });
    try {
      const sig = await getWalletSignature();
      if (!sig) {
        toast.error(t("dashboard.toastSignatureFailed"), { id: toastId });
        return;
      }
      const res = await cancelSubscription({ signature: sig.signature });
      if (res.ok) {
        toast.success(t("dashboard.toastCancelSubscriptionOk"), { id: toastId });
        void refreshOverview();
      } else {
        toast.error(
          res.error.message || t("dashboard.toastCancelSubscriptionFail"),
          { id: toastId }
        );
      }
    } catch (error) {
      const code = classifyWalletError(error);
      if (code === "userRejected") {
        toast.info(t("dashboard.toastSignRejected"), { id: toastId });
        return;
      }
      console.error("[Dashboard] cancelSubscription error:", error);
      // 钱包层错误给精确提示；其它（后端失败）沿用取消订阅失败文案。
      toast.error(
        code === "unknown"
          ? t("dashboard.toastCancelSubscriptionFail")
          : t(getWalletErrorMessageKey(code)),
        { id: toastId }
      );
    } finally {
      setIsStoppingSubscription(false);
    }
  };

  const closeWithdrawModal = () => {
    if (isWithdrawing) return;
    setIsWithdrawModalOpen(false);
    setWithdrawAmount("");
  };

  const handleWithdraw = async () => {
    if (isWithdrawing) return;

    if (!isValidWithdrawAmountForSubmit(withdrawAmount, balanceAvailable)) {
      toast.error(t("dashboard.toastWithdrawAmountInvalid"));
      return;
    }

    setIsWithdrawing(true);
    const toastId = "withdraw";
    toast.loading(t("dashboard.withdraw"), { id: toastId });
    try {
      const sig = await getWalletSignature();
      if (!sig) {
        toast.error(t("dashboard.toastSignatureFailed"), { id: toastId });
        return;
      }
      const res = await createWithdrawal({
        amount: withdrawAmount,
        signature: sig.signature,
      });
      if (res.ok) {
        toast.success(t("dashboard.toastWithdrawOk"), { id: toastId });
        setIsWithdrawModalOpen(false);
        setWithdrawAmount("");
        void refreshOverview();
        void refreshTransactions(txPage);
      } else {
        toast.error(
          res.error.message || t("dashboard.toastWithdrawFail"),
          { id: toastId }
        );
      }
    } catch (error) {
      const code = classifyWalletError(error);
      if (code === "userRejected") {
        toast.info(t("dashboard.toastSignRejected"), { id: toastId });
        return;
      }
      console.error("[Dashboard] withdraw error:", error);
      // 钱包层错误给精确提示；其它（后端失败）沿用提现失败文案。
      toast.error(
        code === "unknown"
          ? t("dashboard.toastWithdrawFail")
          : t(getWalletErrorMessageKey(code)),
        { id: toastId }
      );
    } finally {
      setIsWithdrawing(false);
    }
  };

  const handleEmailAlert = async () => {
    if (isSavingEmail) return;
    if (!EMAIL_PATTERN.test(emailAlert)) {
      // invalidEmail 已在表单内提示
      return;
    }

    setIsSavingEmail(true);
    const toastId = "email-alert";
    toast.loading(t("dashboard.emailAlertModal.save"), { id: toastId });
    try {
      const res = await updateNotificationEmail({ alertEmail: emailAlert });
      if (res.ok) {
        toast.success(t("dashboard.toastEmailSaved"), { id: toastId });
        setIsEmailAlertModalOpen(false);
        // Optimistically reflect the new value, then refetch /settings so the
        // store mirrors the server-side state (handles trimming / normalisation).
        setAlertEmail(emailAlert);
        setEmailAlert("");
        void refreshSettings();
      } else {
        toast.error(
          res.error.message || t("dashboard.toastEmailSaveFail"),
          { id: toastId }
        );
      }
    } catch (error) {
      console.error("[Dashboard] updateNotificationEmail error:", error);
      toast.error(t("dashboard.toastEmailSaveFail"), { id: toastId });
    } finally {
      setIsSavingEmail(false);
    }
  };

  const txTypeLabel = (type: FundTransaction["type"]) => {
    switch (type) {
      case "recharge":
        return t("dashboard.txTypeRecharge");
      case "withdraw":
        return t("dashboard.txTypeWithdraw");
      case "subscription_charge":
        return t("dashboard.txTypeSubscriptionCharge");
      default:
        return t("dashboard.txTypeDeduction");
    }
  };

  const txTypeBadgeClass = (type: FundTransaction["type"]) => {
    if (type === "recharge") {
      return "bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-[#00FFA3]/10 dark:text-[#00FFA3] dark:border-[#00FFA3]/20";
    }
    if (type === "withdraw") {
      return "bg-orange-50 text-orange-600 border-orange-200 dark:bg-orange-500/10 dark:text-orange-400 dark:border-orange-500/20";
    }
    return "bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20";
  };

  // 资金流水状态文案：后端返回 'pending' | 'completed' 原始英文枚举，
  // 这里统一映射到多语言 key，避免直接渲染英文。
  const txStatusLabel = (status: FundTransaction["status"]) => {
    switch (status) {
      case "pending":
        return t("dashboard.txStatusPending");
      case "completed":
        return t("dashboard.txStatusCompleted");
      default:
        return status || "—";
    }
  };

  const txStatusBadgeClass = (status: FundTransaction["status"]) => {
    if (status === "pending") {
      return "bg-amber-50 text-amber-600 border-amber-200 dark:bg-amber-500/10 dark:text-amber-500 dark:border-amber-500/20";
    }
    return "bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-[#00FFA3]/10 dark:text-[#00FFA3] dark:border-[#00FFA3]/20";
  };

  const isEmailInvalid =
    !!emailAlert && !EMAIL_PATTERN.test(emailAlert);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{t("dashboard.title")}</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">{t("dashboard.subtitle")}</p>
        </div>
      </div>

      <DashboardAnnouncementBar announcements={announcements} />

      <div className="space-y-6">
        {/* 当前订阅 + 本月剩余 Credits */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-slate-50 dark:bg-[#141517] rounded-2xl border border-slate-200 dark:border-[#D2F0E1]/5 p-6">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 mb-2">
                  <CreditCard className="w-5 h-5 text-emerald-600 dark:text-[#00FFA3]" />
                  <h2 className="text-sm font-medium">{t("dashboard.currentSubscription")}</h2>
                </div>
                <p className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                  {overviewLoading && !overview ? t("dashboard.loadingOverview") : currentPlan}
                </p>
                {planExpiry !== "—" && (
                  <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50/60 px-3 py-1 dark:border-[#00FFA3]/10 dark:bg-[#00FFA3]/5">
                    <Clock className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-[#00FFA3]" />
                    <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                      {t("dashboard.subscriptionExpiration", { date: planExpiry })}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-3 xl:shrink-0 xl:flex-col xl:items-end xl:gap-2">
                {isSubscriptionActive ? (
                  <>
                    {isPendingChange ? (
                      // 已有待生效降级计划：视觉 disabled + 点击弹 toast 说明原因
                      // 不用原生 `disabled`，避免 onClick 被浏览器吞掉
                      <button
                        type="button"
                        onClick={() =>
                          toast.warning(t("recharge.toastPendingChangeLocked"))
                        }
                        title={t("dashboard.tooltipPendingChangeLocked")}
                        aria-disabled="true"
                        className="bg-slate-300 dark:bg-slate-700 text-slate-500 dark:text-slate-500 px-4 py-2 rounded-full font-bold text-sm text-center whitespace-nowrap cursor-not-allowed"
                      >
                        {t("dashboard.changePlan")}
                      </button>
                    ) : (
                      <Link
                        to={`/recharge?currentPlanId=${encodeURIComponent(subscription?.planId ?? "")}`}
                        className="bg-emerald-500 dark:bg-[#00FFA3] text-white dark:text-[#030E12] px-4 py-2 rounded-full font-bold text-sm text-center whitespace-nowrap hover:opacity-90 transition-colors"
                      >
                        {t("dashboard.changePlan")}
                      </Link>
                    )}
                    {!isFreePlan && (
                      <button
                        type="button"
                        onClick={() => {
                          // 正在提交（isStoppingSubscription=true）时 `disabled` 已吞掉 click，这里是兜底
                          if (isStoppingSubscription) return;
                          if (isPendingChange) {
                            toast.warning(
                              t("recharge.toastPendingChangeLocked")
                            );
                            return;
                          }
                          void handleStopSubscription();
                        }}
                        // 仅 isStoppingSubscription 时真 disabled，防止重复提交；
                        // isPendingChange 用 aria-disabled 表达语义，按钮仍可点击触发 toast
                        disabled={isStoppingSubscription}
                        aria-disabled={isStoppingSubscription || isPendingChange}
                        title={
                          isPendingChange
                            ? t("dashboard.tooltipPendingChangeLocked")
                            : undefined
                        }
                        className="border border-slate-300 dark:border-[#D2F0E1]/5 text-slate-700 dark:text-slate-300 px-4 py-2 rounded-full font-bold text-sm text-center whitespace-nowrap hover:bg-slate-100 dark:hover:bg-[#0E0F11]/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
                      >
                        {t("dashboard.stopSubscription")}
                      </button>
                    )}
                  </>
                ) : (
                  <Link
                    to={`/recharge?currentPlanId=${encodeURIComponent(subscription?.planId ?? "")}`}
                    className="bg-emerald-500 dark:bg-[#00FFA3] text-white dark:text-[#030E12] px-5 py-2 rounded-full font-bold text-sm text-center whitespace-nowrap hover:opacity-90 transition-colors"
                  >
                    {t("dashboard.subscribe")}
                  </Link>
                )}
              </div>
            </div>

            {pendingPlan && !pendingPlan.isFree && (
              <div className="mt-3 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-xl p-3">
                <div className="flex items-start gap-2">
                  <ArrowRight className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-bold text-blue-900 dark:text-blue-200">
                      {t("dashboard.switchingTo", { name: pendingPlan.name })}
                    </p>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
                      {t("dashboard.effectiveDate", { date: pendingPlan.effectiveDate })}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {willCancelAtPeriodEnd && (
              <div className="mt-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-xl p-3">
                <p className="text-sm font-bold text-amber-900 dark:text-amber-200">
                  {pendingPlan?.isFree
                    ? t("dashboard.pendingDowngradeToFree")
                    : t("dashboard.stoppingSubscription")}
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                  {t("dashboard.effectiveDate", {
                    date: pendingPlan?.effectiveDate ?? planExpiry,
                  })}
                </p>
              </div>
            )}

            {!isSubscriptionActive && !willCancelAtPeriodEnd && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-3">
                {t("dashboard.subscriptionStopped")}
              </p>
            )}
          </div>

          <div className="bg-slate-50 dark:bg-[#141517] rounded-2xl border border-slate-200 dark:border-[#D2F0E1]/5 p-6">
            <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 mb-2">
              <Activity className="w-5 h-5 text-emerald-600 dark:text-[#00FFA3]" />
              <h2 className="text-sm font-medium">{t("dashboard.remainingCreditsMonth")}</h2>
            </div>
            <p className="text-3xl font-extrabold text-slate-900 dark:text-white mt-1 tracking-tight">
              {overviewLoading && !overview ? t("dashboard.loadingOverview") : remainingCredits}
            </p>
            {nextCreditResetDate !== "—" && (
              <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50/60 px-3 py-1 dark:border-[#00FFA3]/10 dark:bg-[#00FFA3]/5">
                <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-[#00FFA3]" />
                <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                  {t("dashboard.validUntil")} {nextCreditResetDate}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* 余额 + 邮件告警 */}
        <div className="bg-slate-50 dark:bg-[#141517] rounded-2xl border border-slate-200 dark:border-[#D2F0E1]/5 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
            <div>
              <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 mb-2">
                <Wallet className="w-5 h-5 text-emerald-600 dark:text-[#00FFA3]" />
                <h2 className="text-sm font-medium">{t("dashboard.balanceUsdt")}</h2>
              </div>
              <p className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                {overviewLoading && !overview
                  ? t("dashboard.loadingOverview")
                  : `${formatUsdt(balanceAvailable)} USDT`}
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 mb-2">
                <Mail className="w-5 h-5 text-emerald-600 dark:text-[#00FFA3]" />
                <h2 className="text-sm font-medium">{t("dashboard.emailAlert")}</h2>
              </div>
              {isLoadingSettings ? (
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="h-6 w-48 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
                  <div className="h-6 w-20 rounded-full bg-slate-200 dark:bg-slate-700 animate-pulse" />
                </div>
              ) : currentAlertEmail ? (
                <div className="flex items-center gap-3 flex-wrap">
                  <p
                    className="text-base font-bold text-slate-900 dark:text-white tracking-tight break-all"
                    title={currentAlertEmail}
                  >
                    {currentAlertEmail}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setEmailAlert(currentAlertEmail);
                      setIsEmailAlertModalOpen(true);
                    }}
                    className="bg-emerald-500 dark:bg-[#00FFA3] text-white dark:text-[#030E12] px-3 py-1.5 rounded-full text-xs font-bold hover:opacity-90 transition-all"
                  >
                    {t("dashboard.edit")}
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="text-base font-bold text-slate-400 dark:text-slate-500 tracking-tight">
                    {t("dashboard.notConfigured")}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setEmailAlert("");
                      setIsEmailAlertModalOpen(true);
                    }}
                    className="bg-emerald-500 dark:bg-[#00FFA3] text-white dark:text-[#030E12] px-3 py-1.5 rounded-full text-xs font-bold hover:opacity-90 transition-all"
                  >
                    {t("dashboard.setupAlert")}
                  </button>
                </div>
              )}
            </div>
          </div>

          <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed mb-4 border-t border-slate-200 dark:border-[#D2F0E1]/5 pt-4">
            {t("dashboard.autoRenewNotice")}
          </p>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setIsRechargeModalOpen(true)}
              className="flex-1 bg-emerald-500 dark:bg-[#00FFA3] text-white dark:text-[#030E12] px-5 py-2.5 rounded-full font-bold hover:opacity-90 transition-colors flex items-center justify-center gap-2"
            >
              <TrendingUp className="w-4 h-4" />
              {t("dashboard.recharge")}
            </button>
            <button
              type="button"
              onClick={() => setIsWithdrawModalOpen(true)}
              className="flex-1 border border-slate-300 dark:border-[#D2F0E1]/5 text-slate-700 dark:text-slate-300 px-5 py-2.5 rounded-full font-bold hover:bg-slate-100 dark:hover:bg-[#0E0F11]/50 transition-colors flex items-center justify-center gap-2"
            >
              <TrendingDown className="w-4 h-4" />
              {t("dashboard.withdraw")}
            </button>
          </div>
        </div>

        {/* App Key 用量排行 */}
        <div className="bg-slate-50 dark:bg-[#141517] rounded-2xl border border-slate-200 dark:border-[#D2F0E1]/5 p-6">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">{t("dashboard.keyRanking")}</h2>
            <span className="text-xs font-bold text-emerald-600 dark:text-[#00FFA3] bg-emerald-50 dark:bg-[#00FFA3]/10 px-3 py-1.5 rounded-full border border-emerald-200 dark:border-[#00FFA3]/20">
              {t("dashboard.last30Days")}
            </span>
          </div>
          <div
            className="h-[256px] flex overflow-hidden"
            role="region"
            aria-label={t("dashboard.usageChartScrollRegion")}
          >
            {isUsageChartFirstLoading ? (
              <div className="flex-1 flex items-center justify-center gap-2 text-slate-500 dark:text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">{t("common.loading")}</span>
              </div>
            ) : !hasUsageData ? (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t("dashboard.noUsageData")}
                </p>
              </div>
            ) : (
              <>
            <div className="shrink-0 h-full" style={{ width: usageChartYAxisLayout.colPx }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={usageData}
                  margin={{
                    top: USAGE_CHART_MARGIN_TOP,
                    right: 2,
                    left: 2,
                    bottom: USAGE_CHART_MARGIN_BOTTOM,
                  }}
                >
                  {/* 与右侧柱图同高的 X 轴占位，否则左侧 Y 绘图区偏高，刻度与横向网格线错位 */}
                  <XAxis
                    type="category"
                    dataKey="name"
                    height={USAGE_CHART_X_AXIS_HEIGHT}
                    axisLine={false}
                    tickLine={false}
                    tick={false}
                    interval={0}
                  />
                  <YAxis
                    type="number"
                    domain={[0, usageChartY.niceMax]}
                    ticks={usageChartY.ticks}
                    allowDataOverflow={false}
                    axisLine={false}
                    tickLine={false}
                    width={usageChartYAxisLayout.yAxisWidth}
                    tick={{
                      fontSize: 11,
                      fill: isDark ? "#606566" : "#64748b",
                      textAnchor: "end",
                      dx: -2,
                    }}
                    tickFormatter={(val) => formatAxisCount(typeof val === "number" ? val : Number(val))}
                  />
                  <Bar dataKey="usage" fill="transparent" isAnimationActive={false} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 min-w-0 overflow-x-auto overflow-y-hidden h-full">
              <div
                className="h-full"
                style={{
                  minWidth: `max(100%, ${usageData.length * usageChartMinSlotComputed}px)`,
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={usageData}
                    margin={{
                      top: USAGE_CHART_MARGIN_TOP,
                      right: 20,
                      left: 0,
                      bottom: USAGE_CHART_MARGIN_BOTTOM,
                    }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke={isDark ? "rgba(210, 240, 225, 0.05)" : "#94a3b8"}
                    />
                    <XAxis
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      interval={0}
                      height={USAGE_CHART_X_AXIS_HEIGHT}
                      tick={renderUsageChartXTick}
                    />
                    <YAxis
                      hide
                      type="number"
                      domain={[0, usageChartY.niceMax]}
                      ticks={usageChartY.ticks}
                      allowDataOverflow={false}
                    />
                    <Tooltip
                      cursor={{ fill: "transparent" }}
                      contentStyle={{
                        borderRadius: "8px",
                        border: `1px solid ${isDark ? "rgba(210, 240, 225, 0.1)" : "#e2e8f0"}`,
                        backgroundColor: isDark ? "rgba(20, 21, 23, 0.95)" : "rgba(255, 255, 255, 0.95)",
                        color: isDark ? "#f8fafc" : "#0f172a",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                        padding: "8px 12px",
                        fontSize: "12px",
                      }}
                      labelStyle={{
                        fontWeight: "600",
                        marginBottom: "4px",
                      }}
                      formatter={(value) => {
                        const n = typeof value === "number" ? value : Number(value);
                        const text = Number.isFinite(n) ? n.toLocaleString() : String(value);
                        return [text, t("dashboard.credits30d")];
                      }}
                    />
                    <Bar
                      dataKey="usage"
                      fill={isDark ? "#00FFA3" : "#059669"}
                      radius={[4, 4, 0, 0]}
                      maxBarSize={36}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
              </>
            )}
          </div>
        </div>

        {/* 服务用量 */}
        <div className="bg-slate-50 dark:bg-[#141517] rounded-2xl border border-slate-200 dark:border-[#D2F0E1]/5 p-6 overflow-hidden flex flex-col">
          <div className="flex items-center gap-4 mb-6">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">{t("dashboard.supportedServices")}</h2>
            <a target="_blank" rel="noopener noreferrer" href={import.meta.env.VITE_APP_DOCS} className="ml-auto text-xs text-emerald-600 dark:text-[#00FFA3] font-bold flex items-center hover:opacity-80 transition-opacity">
              {t("dashboard.docs")} <ExternalLink className="w-3 h-3 ml-1" />
            </a>
          </div>
          <div className="flex flex-col space-y-3">
            {serviceUsage.map((service) => (
              <div
                key={service.id}
                className="flex items-center justify-between p-4 rounded-md bg-white dark:bg-[#0E0F11]/50 border border-slate-200 dark:border-[#D2F0E1]/5 hover:border-emerald-300 dark:hover:border-[#00FFA3]/30 transition-colors group"
              >
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-slate-200">{service.name}</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t("dashboard.requests30d")}</p>
                </div>
                <div className="text-right">
                  <span className="text-lg font-extrabold text-slate-900 dark:text-white block leading-none">{service.requests}</span>
                  <span
                    className={`inline-block mt-1.5 text-[11px] px-1.5 py-0.5 rounded font-bold ${
                      service.trend.startsWith("+")
                        ? "bg-emerald-50 text-emerald-600 dark:bg-[#00FFA3]/20 dark:text-[#00FFA3]"
                        : service.trend.startsWith("-")
                          ? "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400"
                          : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                    }`}
                  >
                    {service.trend}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 资金流水 */}
        <div className="bg-slate-50 dark:bg-[#141517] rounded-2xl border border-slate-200 dark:border-[#D2F0E1]/5 p-6 overflow-hidden flex flex-col">
          <div className="flex items-center gap-2 mb-6">
            <History className="w-5 h-5 text-slate-500 dark:text-slate-400" />
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">{t("dashboard.transactionHistory")}</h2>
          </div>
          <div className="flex-1 overflow-x-auto custom-scrollbar relative">
            <DataTableLoadingOverlay
              loading={txLoading && transactions.length > 0}
            />
            <table className="min-w-full divide-y divide-slate-200 dark:divide-[#D2F0E1]/5">
              <thead>
                <tr>
                  <th scope="col" className="pb-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("dashboard.colDateTime")}
                  </th>
                  <th scope="col" className="pb-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("dashboard.colType")}
                  </th>
                  <th scope="col" className="pb-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("dashboard.colAmount")}
                  </th>
                  <th scope="col" className="pb-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("dashboard.colStatus")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-[#D2F0E1]/5">
                <DataTableStateRow
                  colSpan={4}
                  loading={txLoading && transactions.length === 0}
                  isEmpty={!txLoading && transactions.length === 0}
                  emptyTitle={t("dashboard.noTransactions")}
                />
                {transactions.length > 0 && (
                  transactions.map((item) => {
                    const dateText = formatDateTime(item.createdAt);
                    const [datePart, timePart] = dateText.split(" ");
                    const amt = txAmountDisplay(item);
                    return (
                      <tr key={item.id} className="hover:bg-white dark:hover:bg-[#D2F0E1]/5 transition-colors">
                        <td className="py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400 font-mono">
                          <div className="flex flex-col gap-0.5">
                            <span>{datePart}</span>
                            <span className="text-xs opacity-75">{timePart}</span>
                          </div>
                        </td>
                        <td className="py-4 whitespace-nowrap">
                          <span className={`px-2.5 py-1 inline-flex text-[11px] leading-4 font-bold rounded-md border ${txTypeBadgeClass(item.type)}`}>
                            {txTypeLabel(item.type)}
                          </span>
                        </td>
                        <td className="py-4 whitespace-nowrap text-sm font-bold">
                          <span className={amt.positive ? "text-emerald-600 dark:text-[#00FFA3]" : "text-red-600 dark:text-red-400"}>
                            {amt.text}
                          </span>
                        </td>
                        <td className="py-4 whitespace-nowrap">
                          <span className={`px-2.5 py-1 inline-flex text-[11px] leading-4 font-bold rounded-md border ${txStatusBadgeClass(item.status)}`}>
                            {txStatusLabel(item.status)}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <TablePaginationFooter
            page={txPage}
            totalPages={txTotalPages}
            totalItems={txTotal}
            onPageChange={(p) => void refreshTransactions(p)}
          />
        </div>
      </div>

      <RechargeModal
        open={isRechargeModalOpen}
        onClose={() => setIsRechargeModalOpen(false)}
        onSuccess={() => {
          void refreshOverview();
          void refreshTransactions(txPage);
        }}
      />

      {/* Withdraw Modal */}
      {isWithdrawModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-50 dark:bg-[#141517] rounded-2xl border border-slate-200 dark:border-[#D2F0E1]/5 max-w-md w-full p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">{t("dashboard.withdrawModal.title")}</h3>
              <button
                type="button"
                onClick={closeWithdrawModal}
                disabled={isWithdrawing}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
                  {t("dashboard.rechargeModal.amountLabel")}
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={withdrawAmount}
                  onChange={(e) =>
                    setWithdrawAmount(
                      sanitizeWithdrawAmountInput(e.target.value, balanceAvailable)
                    )
                  }
                  placeholder={t("dashboard.rechargeModal.amountPlaceholder")}
                  disabled={isWithdrawing}
                  className="w-full px-4 py-3 bg-white dark:bg-[#0E0F11] border border-slate-300 dark:border-[#D2F0E1]/5 rounded-xl focus:ring-2 focus:ring-emerald-400 dark:focus:ring-[#00FFA3]/50 focus:border-emerald-500 dark:focus:border-[#00FFA3] outline-none text-slate-900 dark:text-slate-100 disabled:opacity-50"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {t("dashboard.withdrawModal.available", { balance: formatUsdt(balanceAvailable) })}
                </p>
              </div>
              {isWithdrawNetworkLoading ? (
                <NetworkInfoCardSkeleton />
              ) : (
                <NetworkInfoCard
                  name={withdrawChain.chainName}
                  logoUrl={withdrawChain.chainLogoUrl}
                />
              )}
            </div>

            <button
              type="button"
              onClick={handleWithdraw}
              disabled={isWithdrawing || !isWithdrawAmountSubmittable}
              className="w-full bg-emerald-500 dark:bg-[#00FFA3] text-white dark:text-[#030E12] px-4 py-3 rounded-full font-bold hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("dashboard.rechargeModal.confirm")}
            </button>
          </div>
        </div>
      )}

      {/* Email Alert Modal */}
      {isEmailAlertModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-50 dark:bg-[#141517] rounded-2xl border border-slate-200 dark:border-[#D2F0E1]/5 max-w-md w-full p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">{t("dashboard.emailAlertModal.title")}</h3>
              <button
                type="button"
                onClick={() => setIsEmailAlertModalOpen(false)}
                disabled={isSavingEmail}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
                  {t("dashboard.emailAlertModal.emailLabel")}
                </label>
                <input
                  type="email"
                  value={emailAlert}
                  onChange={(e) => setEmailAlert(e.target.value)}
                  placeholder={t("dashboard.emailAlertModal.emailPlaceholder")}
                  disabled={isSavingEmail}
                  className="w-full px-4 py-3 bg-white dark:bg-[#0E0F11] border border-slate-300 dark:border-[#D2F0E1]/5 rounded-xl focus:ring-2 focus:ring-emerald-400 dark:focus:ring-[#00FFA3]/50 focus:border-emerald-500 dark:focus:border-[#00FFA3] outline-none text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 disabled:opacity-50"
                />
                {isEmailInvalid && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">{t("dashboard.emailAlertModal.invalidEmail")}</p>
                )}
              </div>
              <div className="bg-amber-50 dark:bg-amber-500/10 rounded-xl p-4 border border-amber-200 dark:border-amber-500/20">
                <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
                  <strong>{t("dashboard.emailAlertModal.rulesTitle")}</strong>
                  <br />• {t("dashboard.emailAlertModal.rule1")}
                  <br />• {t("dashboard.emailAlertModal.rule2")}
                  <br />• {t("dashboard.emailAlertModal.rule3")}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleEmailAlert}
              disabled={isSavingEmail || !EMAIL_PATTERN.test(emailAlert)}
              className="w-full bg-emerald-500 dark:bg-[#00FFA3] text-white dark:text-[#030E12] px-4 py-3 rounded-full font-bold hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("dashboard.emailAlertModal.save")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
